import { useEffect, useRef, type ReactNode } from "react";
import { Check, TrainFront, X } from "lucide-react";
import type { CardColor, Ticket } from "../game/types";
import { INK, cityName } from "./Board";

export function TrainCard({
  color,
  count,
  onClick,
  disabled = false,
  small = false,
}: {
  color: CardColor | "back";
  count?: number;
  onClick?: () => void;
  disabled?: boolean;
  small?: boolean;
}) {
  return (
    <button
      className={`train-card ${small ? "small" : ""} ${color === "locomotive" ? "wild" : ""} ${color === "back" ? "card-back" : ""}`}
      style={{ "--card-color": INK[color] ?? "#214f43" } as React.CSSProperties}
      onClick={onClick}
      disabled={disabled || !onClick}
      aria-label={
        color === "back"
          ? "Draw a blind train card"
          : `${color} train card${count !== undefined ? `, ${count} in hand` : ""}`
      }
    >
      <span className="card-corner">
        {color === "locomotive"
          ? "✦"
          : color === "back"
            ? "EU"
            : color.slice(0, 1).toUpperCase()}
      </span>
      <TrainFront className="card-train" strokeWidth={1.25} />
      <span className="card-name">
        {color === "back"
          ? "DRAW"
          : color === "locomotive"
            ? "WILD"
            : color.toUpperCase()}
      </span>
      {count !== undefined && <span className="card-count">{count}</span>}
    </button>
  );
}

export function Destination({
  ticket,
  selected,
  completed,
  onClick,
}: {
  ticket: Ticket;
  selected?: boolean;
  completed?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      className={`destination ${selected ? "chosen" : ""} ${completed ? "completed" : ""}`}
      onClick={onClick}
      aria-pressed={selected}
    >
      <span className="ticket-points">
        {ticket.points}
        <small>PTS</small>
      </span>
      <span className="ticket-cities">
        <span>{cityName(ticket.from)}</span>
        <i>↓</i>
        <span>{cityName(ticket.to)}</span>
      </span>
      <span className="ticket-end">
        {selected || completed ? (
          <Check size={17} />
        ) : (
          <span className="ticket-dot" />
        )}
        <small>
          {completed ? "CONNECTED" : ticket.long ? "LONG ROUTE" : "DESTINATION"}
        </small>
      </span>
    </button>
  );
}

export function Modal({
  title,
  children,
  onClose,
  wide = false,
}: {
  title: string;
  children: ReactNode;
  onClose?: () => void;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    ref.current?.showModal();
    return () => ref.current?.close();
  }, []);
  return (
    <dialog
      ref={ref}
      className={`modal ${wide ? "wide" : ""}`}
      onCancel={(e) => {
        e.preventDefault();
        onClose?.();
      }}
      aria-label={title}
    >
      <div className="modal-heading">
        <h2>{title}</h2>
        {onClose && (
          <button
            className="icon-button"
            onClick={onClose}
            aria-label="Close dialog"
          >
            <X size={20} />
          </button>
        )}
      </div>
      {children}
    </dialog>
  );
}

export function Rules({ onClose }: { onClose: () => void }) {
  return (
    <Modal title="Your guide to Europe" onClose={onClose} wide>
      <p className="modal-intro">
        Connect cities. Complete tickets. Take the scenic route.
      </p>
      <div className="rule-grid">
        <article>
          <b>01</b>
          <h3>Choose your destinations</h3>
          <p>
            Start with four train cards, 45 trains, three stations, and four
            destination tickets. Keep at least two tickets; you can keep your
            long route or return it. Your destinations stay private.
          </p>
        </article>
        <article>
          <b>02</b>
          <h3>One action each turn</h3>
          <p>
            Draw two train cards, claim one route, draw destination tickets, or
            build a station. A face-up locomotive uses your entire draw action.
            A blind locomotive counts as one card.
          </p>
        </article>
        <article>
          <b>03</b>
          <h3>Claim the railway</h3>
          <p>
            Pay one matching card per space. Gray routes accept any single
            color; locomotives are wild. Ferry symbols require that many
            locomotives. Double routes need 4–5 players for both tracks to be
            used; nobody can own both.
          </p>
        </article>
        <article>
          <b>04</b>
          <h3>Take a tunnel</h3>
          <p>
            Three cards are revealed from the deck. Each card matching your
            payment color, or a locomotive, adds one to the cost. Pay the extra
            cards or withdraw and lose the turn. An all-locomotive attempt only
            matches revealed locomotives.
          </p>
        </article>
        <article>
          <b>05</b>
          <h3>Make connections</h3>
          <p>
            Draw three new tickets and keep at least one. Stations cost one,
            two, then three matching cards. Each station lets your tickets use
            one opponent’s route touching that city. The game finds the best
            consistent station choices when scoring.
          </p>
        </article>
        <article>
          <b>06</b>
          <h3>A grand finale</h3>
          <p>
            Once someone has two trains or fewer, everyone—including that
            player—gets one final turn. Add completed tickets, subtract
            incomplete ones, add four points per unused station, and award ten
            points for the longest continuous trail.
          </p>
        </article>
      </div>
      <div className="score-guide">
        <span>ROUTE LENGTH</span>
        {[
          [1, 1],
          [2, 2],
          [3, 4],
          [4, 7],
          [6, 15],
          [8, 21],
        ].map(([l, p]) => (
          <div key={l}>
            <strong>{l}</strong>
            <small>{p} pts</small>
          </div>
        ))}
      </div>
      <p className="fine-print">
        Ties break by completed tickets, then unused stations, then longest-path
        bonus. This is an independent, unofficial implementation of the base
        Europe game.
      </p>
    </Modal>
  );
}
